import React, { useEffect, useState } from "react";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { downloadCariStatementPdf, downloadCariStatementXlsx } from "@/lib/cariStatement";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", type: "customer", email: "", phone: "", tax_number: "" };

export default function Cari() {
  const { t, fmt, fmtDate } = useI18n();
  const { canEdit, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [stmt, setStmt] = useState(null);
  const [stmtFrom, setStmtFrom] = useState("");
  const [stmtTo, setStmtTo] = useState("");
  const [stmtBusy, setStmtBusy] = useState("");

  const load = () => api.get("/cari").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setError(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setError(""); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/cari/${editing.id}`, form);
      else await api.post("/cari", form);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/cari/${id}`);
    load().catch(() => {});
  };

  const openStatement = (cari) => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
    setStmt(cari);
    setStmtFrom(from);
    setStmtTo(to);
  };

  const runStatement = async (kind) => {
    setStmtBusy(kind);
    try {
      const { data } = await api.get(`/cari/${stmt.id}/statement`, { params: { from: stmtFrom, to: stmtTo } });
      if (kind === "pdf") await downloadCariStatementPdf({ t, fmt, data, userName: user?.name });
      else downloadCariStatementXlsx({ t, data });
      setStmt(null);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setStmtBusy("");
    }
  };

  return (
    <div className="space-y-5" data-testid="cari-page">
      <div className="flex justify-end">
        {canEdit && (
          <Button onClick={openNew} data-testid="cari-new-button">
            <Plus className="mr-2 h-4 w-4" />{t.cariP.new}
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="cari-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3.5 font-medium">{t.app.name}</th>
              <th className="px-5 py-3.5 font-medium">{t.cariP.type}</th>
              <th className="hidden px-5 py-3.5 font-medium md:table-cell">{t.app.email}</th>
              <th className="hidden px-5 py-3.5 font-medium lg:table-cell">{t.cariP.tax}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.cariP.balance}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-400">{t.app.noData}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`cari-row-${r.id}`}>
                <td className="px-5 py-3.5 font-medium text-slate-900">{r.name}</td>
                <td className="px-5 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    r.type === "customer" ? "bg-brand-soft text-brand" : "bg-amber-50 text-amber-600"
                  }`}>
                    {r.type === "customer" ? t.app.customer : t.app.supplier}
                  </span>
                </td>
                <td className="hidden px-5 py-3.5 text-slate-500 md:table-cell">{r.email || "—"}</td>
                <td className="hidden px-5 py-3.5 font-mono text-xs text-slate-500 lg:table-cell">{r.tax_number || "—"}</td>
                <td className={`tnum whitespace-nowrap px-5 py-3.5 text-right font-mono font-medium ${r.balance > 0 ? "text-slate-900" : "text-slate-400"}`}>
                  {r.balance > 0 ? fmt(r.balance) : "—"}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 text-right">
                  <button onClick={() => openStatement(r)} aria-label={t.cariP.statement} title={t.cariP.statement}
                    data-testid={`cari-statement-${r.id}`}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-brand-soft hover:text-brand">
                    <FileText className="h-4 w-4" />
                  </button>
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(r)} aria-label={t.app.edit} data-testid={`cari-edit-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(r.id)} aria-label={t.app.delete} data-testid={`cari-delete-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="cari-dialog">
          <DialogHeader><DialogTitle>{editing ? t.cariP.edit : t.cariP.new}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.name}</Label>
                <Input value={form.name} onChange={set("name")} data-testid="cari-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.cariP.type}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} data-testid="cari-type-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">{t.app.customer}</SelectItem>
                    <SelectItem value="supplier">{t.app.supplier}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.email}</Label>
                <Input type="email" value={form.email || ""} onChange={set("email")} data-testid="cari-email-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.cariP.phone}</Label>
                <Input value={form.phone || ""} onChange={set("phone")} data-testid="cari-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.cariP.tax}</Label>
              <Input value={form.tax_number || ""} onChange={set("tax_number")} data-testid="cari-tax-input" />
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="cari-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cari-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.name} data-testid="cari-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stmt} onOpenChange={(v) => !v && setStmt(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cari-statement-dialog">
          <DialogHeader><DialogTitle>{t.cariP.statementTitle} — {stmt?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.txn.from}</Label>
                <Input type="date" value={stmtFrom} onChange={(e) => setStmtFrom(e.target.value)} data-testid="statement-from-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.txn.to}</Label>
                <Input type="date" value={stmtTo} onChange={(e) => setStmtTo(e.target.value)} data-testid="statement-to-input" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => runStatement("xlsx")} disabled={!!stmtBusy} data-testid="statement-excel-button">
              {stmtBusy === "xlsx" ? t.pdf.preparing : t.pdf.excel}
            </Button>
            <Button onClick={() => runStatement("pdf")} disabled={!!stmtBusy} data-testid="statement-pdf-button">
              {stmtBusy === "pdf" ? t.pdf.preparing : t.pdf.download}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
